import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  clientsAPI,
  serviceCallsAPI,
  cspireAPI,
  franklinAPI,
} from '@shared/apiClient';
import { getErrorMessage } from '@shared/utils';

export const DISPATCH_QUERY_KEYS = [
  'schedulingBacklog',
  'schedulingBacklogSummary',
  'calendarServiceCalls',
  'weeklyAssignments',
  'overdueScheduled',
  'mapPoints',
  'contractClientsNeedingService',
  'cspireWorkOrders',
  'franklinSites',
];

export const useInvalidateDispatch = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    DISPATCH_QUERY_KEYS.forEach((key) => {
      queryClient.invalidateQueries(key);
    });
  }, [queryClient]);
};

export const useDragAssign = () => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateDispatch();

  const assignBacklogToTech = useMutation(
    async ({ item, technicianId, scheduledDate }) => {
      if (!technicianId) throw new Error('Technician is required');

      if (item.source === 'cspire') {
        const payload = { technicianId: String(technicianId) };
        if (scheduledDate) payload.scheduledDate = scheduledDate;
        await cspireAPI.assignWorkOrder(item.refId, payload);
        return;
      }

      if (item.source === 'franklin') {
        const payload = { technicianId: String(technicianId) };
        if (scheduledDate) payload.scheduledDate = scheduledDate;
        await franklinAPI.assignSite(item.refId, payload);
        return;
      }

      if (item.serviceCallId) {
        if (scheduledDate) {
          await serviceCallsAPI.scheduleServiceCall(item.serviceCallId, {
            technicianId: String(technicianId),
            scheduledDate,
          });
        } else {
          await clientsAPI.assignContractClient({
            clientId: String(item.clientId),
            technicianId: String(technicianId),
            serviceCallId: item.serviceCallId,
            notes: '',
            isPriority: item.subgroup === 'repair_queue' || item.isPriority,
          });
        }
        return;
      }

      if (item.clientId) {
        await clientsAPI.assignContractClient({
          clientId: String(item.clientId),
          technicianId: String(technicianId),
          notes: '',
          isPriority: item.isPriority || false,
        });
      }
    },
    {
      onSuccess: () => {
        toast.success('Assigned successfully');
        invalidate();
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const reassignAssignment = useMutation(
    async ({ assignment, technicianId }) => {
      if (assignment.isCspire && assignment.cspireWorkOrder) {
        await serviceCallsAPI.updateServiceCall(assignment.id, {
          technicianId: String(technicianId),
        });
        return;
      }

      if (assignment.isFranklin && assignment.franklinSite) {
        await serviceCallsAPI.updateServiceCall(assignment.id, {
          technicianId: String(technicianId),
        });
        return;
      }

      const client = assignment.client;
      if (!client?.id) throw new Error('Customer information is missing');

      await clientsAPI.assignContractClient({
        clientId: String(client.id),
        technicianId: String(technicianId),
        notes: assignment.customerNotes || '',
        ...(assignment.id ? { serviceCallId: assignment.id } : {}),
      });
    },
    {
      onSuccess: () => {
        toast.success('Reassigned successfully');
        invalidate();
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const unassignAssignment = useMutation(
    async (serviceCallId) => {
      await clientsAPI.unassignContractClient(serviceCallId);
    },
    {
      onSuccess: () => {
        toast.success('Returned to backlog');
        invalidate();
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const scheduleExistingCall = useMutation(
    ({ id, data }) => serviceCallsAPI.scheduleServiceCall(id, data),
    {
      onSuccess: () => {
        toast.success('Schedule updated');
        invalidate();
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const unscheduleCall = useMutation(
    (id) => serviceCallsAPI.unscheduleServiceCall(id),
    {
      onSuccess: () => {
        toast.success('Returned to backlog');
        invalidate();
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  return {
    assignBacklogToTech,
    reassignAssignment,
    unassignAssignment,
    scheduleExistingCall,
    unscheduleCall,
    invalidate,
  };
};
